import React, { useCallback, useMemo, useRef, useState } from "react";
import { GridApiPremium } from "@mui/x-data-grid-premium";
import { Confirm } from "semantic-ui-react";
import {
  AddLink as AddLinkIcon,
  NoteAdd as NoteAddIcon,
  ContentCopy as ContentCopyIcon,
} from "@mui/icons-material";
import ServerDataGrid from "./ServerDataGrid";
import type { default as ServerDataGridComponent } from "./ServerDataGrid";
import { AddAsinToProduct } from "../forms/CreateProductWizard/AddAsinToProduct";
import { CreateProductWizard } from "../forms/CreateProductWizard/CreateProductWizard";
import type { CreateMenuItem } from "./types";

export interface SupplyUnit {
  upc: string;
  supply_title: string;
  part_number: string;
  inventory_id: number;
  inventory_title: string | null;
}

interface ProductWizardData {
  product_id?: number;
  title: string;
  asin: string;
  partNumber: string;
  families: Array<{ id: number; title: string }>;
}

type ServerDataGridProps = React.ComponentProps<typeof ServerDataGridComponent>;

export const ListingsDataGrid = React.memo(function ListingsDataGrid({
  getRowId = (row: any) => row.amazon_listings.listing_id,
  enableDuplicate = false,
  enableCreate: _enableCreate = false,
  enableEdit = true,
  enableDelete = true,
  getInventorySelectedRows,
  extraButtonsPanelProps,
  ...otherProps
}: ServerDataGridProps & { getInventorySelectedRows?: () => SupplyUnit[] }) {
  const internalApiRef = useRef<GridApiPremium | null>(null);

  const [addListingProductData, setAddListingProductData] = useState<{
    productId: number | undefined;
    productTitle: string;
  } | null>(null);

  // State for CreateProductWizard
  const [displayProductWizard, setDisplayProductWizard] = useState(false);
  const [initialProduct, setInitialProduct] =
    useState<ProductWizardData | null>(null);
  const [productFormMode, setProductFormMode] = useState<
    "create" | "duplicate" | "edit"
  >("create");
  const [productFormPage, setProductFormPage] = useState(1);
  const [preselectedSupplyUnits, setPreselectedSupplyUnits] = useState<
    SupplyUnit[]
  >([]);
  const [confirmCloseModalOpen, setConfirmCloseModalOpen] = useState(false);

  const handleOpenAddListing = useCallback(() => {
    const selected = Array.from(
      internalApiRef.current?.getSelectedRows().values() ?? []
    );
    if (selected.length > 1) return;
    if (selected.length === 1) {
      const row = selected[0];
      setAddListingProductData({
        productId: row.product?.product_id,
        productTitle: row.product?.product_title ?? "",
      });
    } else {
      setAddListingProductData({ productId: undefined, productTitle: "" });
    }
  }, []);

  const handleCloseAddListing = useCallback((didCreate?: boolean) => {
    setAddListingProductData(null);
    if (didCreate) {
      internalApiRef.current?.dataSource?.fetchRows?.();
    }
  }, []);

  // Transform row data to the shape CreateProductWizard expects
  const shapeProductDataForWizard = useCallback(
    (row: any): ProductWizardData => {
      return {
        product_id: row.product?.product_id,
        title: row.product?.product_title || "",
        partNumber: row.product?.assembly_part_number || "",
        asin: row.amazon_listings?.asin || "",
        families:
          row.product_families?.map((subRow: any) => ({
            id: subRow.value,
            title: subRow.label,
          })) || [],
      };
    },
    []
  );

  const handleCreateNew = useCallback(() => {
    setInitialProduct(null);
    setPreselectedSupplyUnits(getInventorySelectedRows?.() ?? []);
    setProductFormMode("create");
    setProductFormPage(1);
    setDisplayProductWizard(true);
  }, [getInventorySelectedRows]);

  const handleDuplicate = useCallback(() => {
    const selected = Array.from(
      internalApiRef.current?.getSelectedRows().values() ?? []
    );
    if (selected.length !== 1) return;
    const productData = shapeProductDataForWizard(selected[0]);
    setInitialProduct(productData);
    setPreselectedSupplyUnits([]);
    setProductFormMode("duplicate");
    setProductFormPage(1);
    setDisplayProductWizard(true);
  }, [shapeProductDataForWizard]);

  const closeCreateForm = useCallback((skipConfirmation?: boolean) => {
    if (skipConfirmation) {
      setDisplayProductWizard(false);
      setInitialProduct(null);
      setPreselectedSupplyUnits([]);
      setProductFormMode("create");
      setProductFormPage(1);
    } else {
      setConfirmCloseModalOpen(true);
    }
  }, []);

  const handleConfirmClose = useCallback(() => {
    setInitialProduct(null);
    setDisplayProductWizard(false);
    setConfirmCloseModalOpen(false);
    setPreselectedSupplyUnits([]);
    setProductFormMode("create");
    setProductFormPage(1);
  }, []);

  const handleConfirmCloseCancel = useCallback(() => {
    setConfirmCloseModalOpen(false);
  }, []);

  const createMenuItems = useMemo<CreateMenuItem[]>(
    () => [
      {
        label: "Create New Product",
        icon: <NoteAddIcon fontSize="small" />,
        onClick: handleCreateNew,
      },
      {
        label: "Duplicate Existing Product",
        icon: <ContentCopyIcon fontSize="small" />,
        onClick: handleDuplicate,
        requiresRowSelection: true,
        disabledReason: "Select 1 row",
      },
      {
        label: "Add ASIN to Product",
        icon: <AddLinkIcon fontSize="small" />,
        onClick: handleOpenAddListing,
        disabledWhenMultipleSelected: true,
        disabledReason: "Select at most 1 row",
      },
    ],
    [handleCreateNew, handleDuplicate, handleOpenAddListing]
  );

  return (
    <>
      <Confirm
        open={confirmCloseModalOpen}
        content="Are you sure you want to close the create form? Any unsaved changes will be lost."
        onCancel={handleConfirmCloseCancel}
        onConfirm={handleConfirmClose}
      />

      {displayProductWizard && (
        <CreateProductWizard
          onClose={closeCreateForm}
          preselectedSupplyUnits={preselectedSupplyUnits}
          initialProduct={initialProduct}
          initialPage={productFormPage}
          mode={productFormMode}
        />
      )}

      {addListingProductData && (
        <AddAsinToProduct
          productId={addListingProductData.productId}
          productTitle={addListingProductData.productTitle}
          onClose={handleCloseAddListing}
        />
      )}

      <ServerDataGrid
        {...otherProps}
        getRowId={getRowId}
        enableCreate={true}
        enableDuplicate={enableDuplicate}
        enableEdit={enableEdit}
        enableDelete={enableDelete}
        extraButtonsPanelProps={extraButtonsPanelProps}
        createMenuItems={createMenuItems}
        apiRef={internalApiRef}
      />
    </>
  );
});
